<script setup lang="ts">

import ToolTip from "@/components/UI/tooltip/ToolTip.vue";
import { RiArrowRightSLine, RiInformationLine } from "@remixicon/vue";
import CompanyActivityCard from "@/components/dashboard/CompanyActivityCard.vue";
import CarbonFootprintDoughnut from "@/components/dashboard/CarbonFootprintDoughnut.vue";
import BarChart from "@/components/analysis/BarChart.vue";
import { useCompanyStore } from "@/stores/company";
import { useCompanyFacilities } from "@/stores/facilities";
import { useAnalytic } from "@/stores/analysis";
import { useI18n } from "vue-i18n";
import { watch } from "vue";
import { hasSelectedCompany } from "@/utils/companyContext";



const companyStore = useCompanyStore();
const { getCompany } = companyStore;
const facilitiesStore = useCompanyFacilities();
const analytic = useAnalytic();
const { getCompanyFacilities } = facilitiesStore;
const { fetchAnalysis, fetchTimeSeries } = analytic;

const { t } = useI18n();
analytic.$patch({ breakdown: "FACILITY", facilities: [], year: "" });

// On fresh login the company is selected AFTER this component mounts (LayoutDefault patches
// companyStore.companyId asynchronously), so a one-shot onMounted guarded by
// hasSelectedCompany() would bail and never retry. Watching companyStore.companyId (immediate)
// loads on refresh (already set) and on login (set later) alike.
let loadedForCompanyId: string | null = null;
watch(
  () => companyStore.companyId,
  async (companyId) => {
    if (!companyId || !hasSelectedCompany() || loadedForCompanyId === companyId) return;
    loadedForCompanyId = companyId;
    await getCompany();
    await getCompanyFacilities();
    await fetchAnalysis();
    await fetchTimeSeries();
  },
  { immediate: true }
);

</script>

<template>
  <!-- Single row: Carbon footprint (3) + Carbon story (2) + Activity (1) -->
  <div class="grid grid-cols-6 gap-4 items-stretch">
    <!-- Carbon footprint doughnut -->
    <CarbonFootprintDoughnut
      class="col-span-3"
      data-step="1"
      :data-intro="t('dashboard.carbonFootprintToolTip')"
    />

    <!-- Carbon story (BarChart) -->
    <div
      class="col-span-2 border border-neautrals-200 rounded-md bg-white flex flex-col"
      data-step="2"
      data-intro="In this area you can see a summary of your carbon emissions by year."
    >
      <div class="flex p-4 items-center justify-between shadow">
        <span class="flex items-center">
          <span class="mr-2">{{ t("dashboard.carbonStory") }}</span>
          <ToolTip
            placement="bottom"
            :title="$t('dashboard.carbonStory')"
            :content="$t('calculation.tooltip.carbonStory')"
          >
            <RiInformationLine className="w-5 h-5" />
          </ToolTip>
        </span>
        <router-link :to="{ name: 'analysis' }" class="flex items-center">
          <span class="mr-1 underline font-medium">{{ t("dashboard.goIntoDetail") }}</span>
          <RiArrowRightSLine />
        </router-link>
      </div>
      <div class="flex-1 relative p-4">
        <span class="text-xxs text-neautrals-500">tCO2e</span>
        <BarChart />
      </div>
    </div>

    <!-- Activity feed -->
    <div
      class="col-span-1"
      data-step="4"
      data-intro="Keep track of what's happening in your company here."
    >
      <CompanyActivityCard />
    </div>
  </div>
</template>
